import LayoutContainer from 'components/Common/LayoutContainer'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import Heading from 'LuxKit/Typography/Heading'
import useCarHireList from 'hooks/CarHire/useCarHireList'
import React, { useCallback, useMemo } from 'react'
import { sortCarHireOffers } from '../carHireUtils'
import CarHireOfferTileCondensed from '../CarHireOfferTile/CarHireOfferTileCondensed'
import Group from 'components/utils/Group'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import { rem } from 'polished'
import StructureLabel from 'LuxKit/Label/StructureLabel'
import { pluralizeToString } from 'lib/string/pluralize'
import TextButton from 'LuxKit/Button/TextButton'
import qs from 'qs'
import { pushWithRegion } from 'actions/NavigationActions'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { take } from 'lib/array/arrayUtils'
import Pane from 'components/Common/Pane'
import { mediaQueryUp } from 'components/utils/breakpoint'
import styled from 'styled-components'
import moment from 'moment'
import { DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import { dateDifference } from 'lib/datetime/dateUtils'
import { initialiseCheckout } from 'actions/CheckoutActions'
import { generateCarHireCheckoutItem } from '../CarHireCheckoutHelper'
import { getDefaultUserDriverCategory } from 'selectors/carHireSelectors'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import LoadingAnimation from 'components/Common/Loading/LoadingAnimation'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { HotelOrderData } from 'hooks/CarHire/useOrderCarHire'
import { addToCart } from 'analytics/snowplow/events'
import * as Analytics from 'analytics/analytics'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import useCarHireOfferListFilters from 'hooks/CarHire/useCarHireOfferListFilters'

const expired = 0

const UpcomingTripSection = styled(Pane)`
  padding: ${rem(40)} 0;

  ${mediaQueryUp.tablet} {
    padding: ${rem(64)} 0;
  }
`

const LoadingWrapper = styled(Group)`
  height: ${rem(240)};
`

interface Props {
  orderView: HotelOrderData
}

function CarHirePageCarousels(props: Props) {
  const { orderView } = props

  const checkIn = moment(orderView.dates.checkIn).format(DMY_CASUAL_SHORT_FORMAT)
  const checkOut = moment(orderView.dates.checkOut).format(DMY_CASUAL_SHORT_FORMAT)
  const daysToGo = dateDifference(new Date(checkIn), new Date()).days
  const pickUpName = orderView.place?.canonicalName

  const [defaultAgeCategory, defaultAge] = useAppSelector(getDefaultUserDriverCategory)
  const dispatch = useAppDispatch()

  const filters = useCarHireOfferListFilters({
    pickUpLocationId: orderView.place?.id,
    dropOffLocationId: orderView.place?.id,
    pickUpDate: checkIn || undefined,
    dropOffDate: checkOut || undefined,
    pickUpTime: '10:00',
    dropOffTime: '10:00',
    driversAgeCategory: defaultAgeCategory,
    isPickUpAtAirport: false,
    pickUpSearchType: 'geo',
    dropOffSearchType: 'geo',
    driversAge: defaultAge,
  })

  const carHireList = useCarHireList(filters, expired)
  const offers = carHireList.vehicles
  const sortedOffers = useMemo(() => take(sortCarHireOffers(offers, 'price.asc'), 6), [offers])

  const onSearch = () => {
    const query = qs.stringify({
      pickUpName,
      dropOffName: pickUpName,
      pickUpId: filters?.pickUpLocationId,
      dropOffId: filters?.dropOffLocationId,
      pickUpDate: filters?.pickUpDate,
      pickUpTime: '10:00',
      dropOffDate: filters?.dropOffDate,
      dropOffTime: '10:00',
      ageCategory: filters?.driversAgeCategory,
      age: filters?.driversAge,
      isPickUpAtAirport: false,
      pickUpSearchType: 'geo',
      dropOffSearchType: 'geo',
    })

    dispatch(pushWithRegion('/search/car-hire', query))
  }

  const onGoToCheckout = useCallback((offer: App.CarHireOffer) => {
    if (filters) {
      const [pickUpDate, pickUpTime] = moment.utc(carHireList.pickUp.date).format('YYYY-MM-DD|HH:mm').split('|')
      const [dropOffDate, dropOffTime] = moment.utc(carHireList.dropOff.date).format('YYYY-MM-DD|HH:mm').split('|')

      const item = generateCarHireCheckoutItem(offer, {
        dropOffDate,
        dropOffTime,
        dropOffLocation: pickUpName,
        dropOffLocationId: filters.pickUpLocationId!,
        pickUpLocation: pickUpName,
        pickUpLocationId: filters.pickUpLocationId!,
        pickUpDate,
        pickUpTime,
        ageCategory: filters.driversAgeCategory!,
        age: filters.driversAge!,
        isPickUpAtAirport: !!filters.isPickUpAtAirport!,
        resultsTimestamp: carHireList.timestamp || 0,
      })

      dispatch(initialiseCheckout([item]))
      Analytics.trackEvent(addToCart())
      dispatch(navigateToCheckout())
    }
  }, [dispatch, filters, pickUpName, carHireList])

  return (
    <>
      {orderView && sortedOffers.length > 0 && (
        <UpcomingTripSection background="neutral-seven">
          <LayoutContainer>
            <VerticalSpacer gap={24}>
              <Group gap={8} direction="vertical">
                <Group direction="horizontal" horizontalAlign="space-between">
                  <Heading variant="heading3">
                    Hire a car while you're in <i>{orderView.place?.name}</i>
                  </Heading>
                  <TextButton kind="tertiary" onClick={onSearch}>
                    View All
                  </TextButton>
                </Group>
                <Group direction="vertical" tabletDirection="horizontal" wrap="wrap" gap={8} fullWidth>
                  <Group direction="horizontal" tabletVerticalAlign="center" gap={8}>
                    <StructureLabel variant="dark">{`${pluralizeToString('Day', daysToGo)}`} to go</StructureLabel>
                    <BodyTextBlock variant="medium">
                      {sortedOffers.length <= 0 &&
                        <>
                          {`${checkIn} - ${checkOut}`}{' '}
                        </>}
                      {sortedOffers.length > 0 &&
                        <>
                          {`${checkIn} - ${checkOut} (${pluralizeToString('Day', sortedOffers[0].duration)})`}{' '}
                        </>}
                      <CSSBreakpoint min="desktop" as="span">
                        {`${pickUpName ? `· Pick up & Drop-off at ${pickUpName}` : ''}`}
                      </CSSBreakpoint>
                    </BodyTextBlock>
                  </Group>
                  {/* mobile */}
                  <CSSBreakpoint max="tablet">
                    <BodyTextBlock variant="medium">
                      {`${pickUpName ? `Pick up & Drop-off at ${pickUpName}` : ''}`}
                    </BodyTextBlock>
                  </CSSBreakpoint>
                </Group>
              </Group>

              {carHireList.fetching &&
                <LoadingWrapper gap={24} direction="vertical" fullWidth horizontalAlign="center" verticalAlign="center">
                  <Heading align="center" variant="heading5">
                    Searching for the best offers for you!
                  </Heading>
                  <LoadingAnimation />
                </LoadingWrapper>}

              {!carHireList.fetching && (
                <CardCarousel width={rem(364)} gap={16}>
                  {!carHireList.fetching && sortedOffers.map((offer) =>
                    <CarHireOfferTileCondensed
                      key={offer.id}
                      offer={offer}
                      onReserve={onGoToCheckout}
                      pickUpDate={checkIn}
                      dropOffDate={checkOut}
                      pickUpTime="10:00"
                      dropOffTime="10:00"
                    />,
                  )}
                </CardCarousel>
              )}
            </VerticalSpacer>

          </LayoutContainer>
        </UpcomingTripSection>
      )}
    </>
  )
}

export default CarHirePageCarousels
